import React, { useEffect, useState, useRef, useContext } from 'react'
import { IMSContext } from '../../list/IMSFormCategorySelect'
import { IMSFormContext } from './IMSFormContext'
import ImsFormFields from './IMSFormFields2'
import {
    IMSFormProps,
    handleFieldChange as handleFieldChangeHelper,
    handleIMSFormSave as handleIMSFormSaveHelper,
} from './imsFormHelpers'
import {
    prepareImsFormsData,
    processFormData,
    prepareUpdatedImsData,
    populateInitialValues,
} from './imsFormDataTransformations'
import { useAssetDrawerStore } from '../AssetPropertiesDrawer/assetDrawerStore'
import { useItemById } from '../../../../services/api'

const IMSForm: React.FC<IMSFormProps> = ({
    selectedCategory,
    evalItem,
    evaluation,
    itemData,
}) => {
    const [formData, setFormData] = useState<any>({})
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState<boolean>(false)
    const { nominationValue, setNominationValue } = useContext(IMSContext)
    const [fieldValues, setFieldValues] = useState<{ [key: string]: any }>({})
    const { imsForm, setImsForm } = useContext(IMSFormContext)
    const [newIMSData, setNewIMSData] = useState<any>(null)
    const [isCategoryChanged, setIsCategoryChanged] = useState<boolean>(false)
    const [opportunityID, setOpportunityID] = useState<string | null>(null)
    const { jobCodeData, setJobCodeData, estimatedHammer, setEstimatedHammer } =
        useContext(IMSContext)
    const { loadForm, saveForm, setSaveForm, setIsSavingIMS } = useAssetDrawerStore()
    const [successAlert, setSuccessAlert] = useState<boolean>(false)

    // Using React Query hook for fetching item data only if itemData is not provided
    const itemQuery = useItemById(evalItem?.item_id, { enabled: !!evalItem?.item_id && !itemData }) as any;
    
    // Use provided itemData or fetched data
    const currentItemData = itemData || itemQuery.data;

    const isMounted = useRef<boolean>(false)

    const handleSaveSuccess = () => {
        setSuccessAlert(true)
    }

    const handleSaveSuccessClose = () => {
        setSuccessAlert(false)
    }

    const handleNominationChange = (
        event: React.ChangeEvent<{ value: unknown }>
    ) => {
        const value = event.target.value
        if (typeof value === 'number') {
            setNominationValue(value.toString())
        }
    }

    useEffect(() => {
        if (selectedCategory && selectedCategory.id) {
            setIsCategoryChanged(true)
        }
    }, [selectedCategory])

    useEffect(() => {
        if (imsForm?.item?.ims_forms_data?.nomination) {
            const nominationStringValue =
                imsForm?.item?.ims_forms_data.nomination.toString()
            setNominationValue(nominationStringValue)
        } else if (evalItem?.item?.ims_forms_data?.nomination) {
            const nominationStringValue =
                evalItem.item.ims_forms_data.nomination.toString()
            setNominationValue(nominationStringValue)
        }
    }, [imsForm, evalItem])

    // Using the helper function but adapting for the component's state
    const handleFieldChange = (
        groupId: any,
        componentId: string | number,
        newValue: string | boolean,
        datatype: any,
        photoCapture = 0
    ) => {
        const updatedFieldValues = handleFieldChangeHelper(
            fieldValues,
            groupId,
            componentId,
            newValue,
            datatype,
            photoCapture
        )
        setFieldValues(updatedFieldValues)
    }

    useEffect(() => {
        let initialValues

        if (imsForm?.item?.ims_forms_data?.groups) {
            initialValues = populateInitialValues(
                imsForm.item.ims_forms_data.groups
            )
        } else if (evalItem?.item?.ims_forms_data?.groups) {
            initialValues = populateInitialValues(
                evalItem.item.ims_forms_data.groups
            )
        } else {
            initialValues = {}
        }

        setFieldValues(initialValues)
    }, [imsForm, evalItem])

    console.log({ imsForm })

    // Using the helper function but adapting for the component's state
    // Update imsForm when itemData changes
    useEffect(() => {
        if (currentItemData) {
            // console.log('RETURNED ITEM ========>', currentItemData);
            setImsForm(currentItemData);
        }
    }, [currentItemData, setImsForm]);
    
    // Update error and loading states from React Query only when not using provided itemData
    useEffect(() => {
        if (!itemData) {
            setError(itemQuery.error ? 'Failed to fetch item data' : null);
            setLoading(itemQuery.isLoading);
        } else {
            setError(null);
            setLoading(false);
        }
    }, [itemQuery.error, itemQuery.isLoading, itemData]);

    useEffect(() => {
        if (evaluation) {
            setOpportunityID(evaluation?.evaluation?.opportunity_id)
        }
    }, [evaluation])

    useEffect(() => {
        if (imsForm && imsForm?.jobcode) {
            setJobCodeData(imsForm?.jobcode)
        } else if (evalItem && evalItem?.jobcode) {
            setJobCodeData(evalItem?.jobcode)
        }
    }, [imsForm, evalItem])

    useEffect(() => {
        if (imsForm?.item?.capture_estimate) {
            setEstimatedHammer(imsForm?.item?.capture_estimate)
        } else if (evalItem && evalItem?.item?.capture_estimate) {
            setEstimatedHammer(evalItem?.item?.capture_estimate)
        }
    }, [imsForm, evalItem])

    useEffect(() => {
        const fetchData = async () => {
            if (!selectedCategory || !selectedCategory.id) return

            try {
                setLoading(true)
                const response = await fetch(
                    `https://ims-api.purplewave.com/v1/forms/category/${selectedCategory.id}`
                )
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`)
                }
                const data = await response.json()
                // console.log('Form data received:', data)

                // Sort groups by category_group.order before processing
                if (
                    data.Categories &&
                    data.Categories[0] &&
                    data.Categories[0].Groups
                ) {
                    data.Categories[0].Groups.sort(
                        (
                            a: { category_group: { order: number } },
                            b: { category_group: { order: number } }
                        ) => {
                            const orderA = a.category_group?.order ?? 999 // Default high value for missing order
                            const orderB = b.category_group?.order ?? 999
                            return orderA - orderB
                        }
                    )
                }

                // Use the extracted function
                const processedData = processFormData(data)

                console.log({ processedData })
                //console.log('Form data received:', processedData)
                setFormData(processedData)

                setError(null)
            } catch (error: unknown) {
                if (error instanceof Error) {
                    console.error('Error fetching form data:', error)
                    setError(error.message)
                } else {
                    console.error('Unknown error occurred', error)
                    setError('An unknown error occurred.')
                }
            } finally {
                setLoading(false)
            }
        }

        fetchData()
    }, [selectedCategory, evalItem])

    // Use the extracted function to prepare the form data
    const ims_forms_data = prepareImsFormsData(
        formData,
        selectedCategory,
        nominationValue,
        jobCodeData,
        opportunityID
    )

    console.log('Prepared data', { ims_forms_data })

    // Create a ref for storing the previous evalItem
    const prevEvalItemRef = useRef(null)

    // Update prevEvalItemRef when evalItem changes
    useEffect(() => {
        prevEvalItemRef.current =
            imsForm?.item?.ims_forms_data &&
            Object.keys(imsForm?.item?.ims_forms_data).length > 0
                ? imsForm
                : evalItem
    }, [evalItem, imsForm])

    // Access the previous value of evalItem using prevEvalItemRef.current
    const prevEvalItem = prevEvalItemRef.current

    const handleIMSFormSave = async () => {
        return handleIMSFormSaveHelper(
            saveForm,
            imsForm,
            formData,
            ims_forms_data,
            fieldValues,
            estimatedHammer,
            evalItem,
            prevEvalItem,
            setSaveForm,
            handleSaveSuccess
        )
    }

    useEffect(() => {
        if (!isMounted.current) {
            isMounted.current = true
            return
        }

        const runSave = async () => {
            try {
                setIsSavingIMS(true)
                await handleIMSFormSave()
            } finally {
                setIsSavingIMS(false)
                setSaveForm(false)
            }
        }

        if (saveForm) {
            runSave()
        }
    }, [saveForm])

    useEffect(() => {
        const updatedImsData = prepareUpdatedImsData(
            imsForm?.item?.ims_forms_data
        )
        setNewIMSData(updatedImsData)
    }, [imsForm, loadForm])

    console.log({
        ims_forms_data,
        imsForm,
        prevEvalItem,
        selectedCategory,
        fieldValues,
        newIMSData,
    })

    return (
        <>
            <ImsFormFields
                loadForm={loadForm}
                selectedCategory={selectedCategory}
                successAlert={successAlert}
                handleSaveSuccessClose={handleSaveSuccessClose}
                imsForm={imsForm}
                loading={loading}
                error={error}
                titleStyle={{
                    fontSize: '16px',
                    marginBottom: '8px',
                    color: '#333',
                }}
                internaUseValues={{ marginTop: '4px' }}
                internaUseValues2={{ marginBottom: '20px' }}
                handleFieldChange={handleFieldChange}
                fieldValues={fieldValues}
                nominationValue={nominationValue}
                handleNominationChange={handleNominationChange}
                newIMSData={newIMSData}
                ims_forms_data={ims_forms_data}
                evaluation={evaluation}
                handleIMSFormSave={handleIMSFormSave}
                setSaveForm={setSaveForm}
                setImsForm={setImsForm}
                prevEvalItem={prevEvalItem}
                handleSaveSuccess={handleSaveSuccess}
                setFieldValues={setFieldValues}
            />
        </>
    )
}

export default IMSForm


